/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * The component for filter Range type
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @version 1.3.1
 * @since 1.0.0
 */

import { ChangeEvent, useEffect, useRef, useState } from 'react';

import invoke from 'lodash/invoke';

import { icons } from '../../../../assets';
import initTranslationCommon from '../../../../modules/internationalization/constants/common';
import ICommonTranslation from '../../../../modules/internationalization/types/common';
import { DropDownFilter, RangeInput } from '../../../common';
import { EInputCursor, EInputKind, EInputType } from '../../../common/Input/Input';
import { ICurrentInputProps } from '../../../common/RangeInput/RangeInput';

type ValueType = number | string;

/**
 * @description Range filter params
 * @interface IFilterRangeParams
 * @property {string} filterValue - filter label value
 * @property {number | string} valueFrom - range value from
 * @property {number | string} valueTo - range value to
 */
export interface IFilterRangeParams {
    filterValue: string;
    valueFrom: ValueType;
    valueTo: ValueType;
}

/**
 * @description Range filter props
 * @interface IFilterRangeProps
 * @property {string} filterValue - filter label value
 * @property {number | string} valueFrom - range value from
 * @property {number | string} valueTo - range value to
 * @property {left: IInputProps, right: IInputProps} inputProps - props for left and right inputs
 * @property {object} messages - locale
 * @property {Function} onChange - change callback
 */
export interface IFilterRangeProps {
    filterValue: string;
    isCurrency: boolean;
    onChange?: (argument: IFilterRangeParams) => void;
    translation: ICommonTranslation;
    valueFrom: ValueType;
    valueTo: ValueType;
}

const defaultProps: Partial<IFilterRangeProps> = {
    filterValue: '',
    isCurrency: false,
    onChange: () => null,
    translation: initTranslationCommon,
    valueFrom: '',
    valueTo: '',
};

/**
 * @description Define filter-range  for filtering subscription in table.
 * @class FilterTableHistorySubscription
 * @extends PureComponent<IFilterRangeProps, IFilterRangeState>
 *
 * @see BlockWithIcon component
 * @see DropDownFilter component
 * @see RangeInput component
 * @see TextBlock component
 */
const FilterRange = (props: IFilterRangeProps) => {
    const MAX_DIGIT_COUNT = 8;

    const {
        filterValue,
        isCurrency,
        translation,
    } = props;
    const regExp = new RegExp(isCurrency ? /^[\-]?\d+(?:[.,][\d]{1,2})?$/ : /^\d+$/);

    const [valueFrom, setValueFrom] = useState(props.valueFrom);
    const [valueFromValid, setValueFromValid] = useState(true);
    const [valueTo, setValueTo] = useState(props.valueTo);
    const [valueToValid, setValueToValid] = useState(true);
    const dropDownRef = useRef(null);

    useEffect(() => {
        setValueFrom(props.valueFrom);
        setValueTo(props.valueTo);
    }, [props.valueTo, props.valueFrom]);

    /**
     * @description Handler for left input.
     * @param{ChangeEvent<HTMLInputElement>} event Changes left from input.
     */
    const inputHandlerLeft = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;

        setValueFrom(text);
        setValueFromValid(isRightValueGreater(text, valueTo) && isValueValid(text));
        setValueToValid(isRightValueGreater(text, valueTo) && isValueValid(valueTo));
    };

    /**
     * @description Handler for right input.
     * @param{ChangeEvent<HTMLInputElement>} event Changes from right input.
     */
    const inputHandlerRight = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;

        setValueTo(text);
        setValueFromValid(isRightValueGreater(valueFrom, text) && isValueValid(valueFrom));
        setValueToValid(isRightValueGreater(valueFrom, text) && isValueValid(text));
    };

    /**
     * @description Handler for ok button in DropDown. Set text and state.
     */
    const okButtonHandler = () => {
        if (valueFromValid && valueToValid && isFilterChanged()) {
            const filterValue = getRangeFilterValue(valueFrom, valueTo);
            invoke(props, 'onChange', {
                filterValue,
                valueFrom,
                valueTo,
            });
        }
    };
    const onEnterHandler = () => {
        invoke(dropDownRef, 'current.componentRef.current.handleOkButtonClick')
    };

    /**
     * @description Handler for click outside of the range input filter
     */
    const onCloseHandler = () => {
        setValueFrom(props.valueFrom);
        setValueFromValid(true);
        setValueTo(props.valueTo);
        setValueToValid(true);
    };

    const isValueValid = (value: ValueType): boolean => {
        if (!value) {
            return true;
        }
        return !!value.toString().match(regExp) && value.toString().length <= MAX_DIGIT_COUNT;
    };

    const isRightValueGreater = (leftValue: ValueType | undefined, rightValue: ValueType | undefined) => {
        if (!leftValue || !rightValue) {
            return true;
        } else {
            const from = leftValue.toString();
            const to = rightValue.toString();
            if (!regExp.test(from) || !regExp.test(to)) {
                return true;
            } else {
                return parseFloat(from) < parseFloat(to);
            }
        }
    };

    const isFilterChanged = () => valueFrom !== props.valueFrom || valueTo !== props.valueTo;

    const inputLeft: ICurrentInputProps = {
        input: {
            autoFocus: true,
            cursor: EInputCursor.auto,
            kind: valueFromValid ? EInputKind.normal : EInputKind.error,
            label: translation.commonTable.from,
            onChange: inputHandlerLeft,
            placeholder: translation.commonTable.from,
            type: EInputType.small,
            value: valueFrom,
        },
    };

    const inputRight: ICurrentInputProps = {
        input: {
            cursor: EInputCursor.auto,
            kind: valueToValid ? EInputKind.normal : EInputKind.error,
            label: translation.commonTable.to,
            onChange: inputHandlerRight,
            placeholder: translation.commonTable.to,
            type: EInputType.small,
            value: valueTo,
        },
    };

    const icon = filterValue ? '' : icons.arrowListDownSmall;

    return (
        <DropDownFilter
            filterButtonLabel={filterValue}
            icon={icon}
            kind={EInputKind.normal}
            okButtonDisabled={(!valueFromValid || !valueToValid)}
            ref={dropDownRef}
            renderInBody={true}
            showCloseButton={true}
            translation={({
                ...translation.dropDown,
                placeholder: translation.range.placeholder,
            })}
            widthAuto={true}
            onCloseClick={onCloseHandler}
            onOkClick={okButtonHandler}
        >
            <RangeInput
                left={inputLeft}
                right={inputRight}
                onEnter={onEnterHandler}
            />
        </DropDownFilter>
    );
};

FilterRange.defaultProps = defaultProps;

export const getRangeFilterValue = (valueFrom: ValueType, valueTo: ValueType): string => {
    const valueFromInt = parseInt(valueFrom as string);
    const valueToInt = parseInt(valueTo as string);
    if (!isNaN(valueFromInt) && !isNaN(valueToInt)) {
        return `${valueFrom} ... ${valueTo}`;
    } else if (!isNaN(valueFromInt) && isNaN(valueToInt)) {
        return `⩾ ${valueFrom}`;
    } else if (isNaN(valueFromInt) && !isNaN(valueToInt)) {
        return `⩽ ${valueTo}`;
    }
    return '';
};

export default FilterRange;
